import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { DevextremesharedmoduleModule } from "../../../../shared/Module/devextremeshared/devextremeshared.module";
import { Router } from "@angular/router";
import { FinancialYear } from "../../../../classes/domain/entities/website/financialyear/financialyearmaster";
import {
  ContraceptivePillsTypes,
  DomainEnums,
  DrillDownRequestTypes,
  DrillDownSubTypes,
  No_Of_ChildrenAgeTypes,
  EventLocationTypes,
  HospitalTypes,
  ReportFilterTypes,
  Roles,
  SterilizedTypes,
} from "../../../../classes/infrastructure/enums";
import { UIUtils } from "../../../../services/uiutils.service";
import { AppStateManageService } from "../../../../services/app-state-manage.service";
import { ScreenSizeService } from "../../../../services/screensize.service";
import { ServerCommunicatorService } from "../../../../services/server-communicator.service";
import { DTU } from "../../../../services/dtu.service";
import { FinancialYearUtilsService } from "../../../../services/current-financial-year.service";
import { SidebarMenuStateService } from "../../../../services/sidebarmenustateservice";
import { LanguageService } from "../../../../services/language.service";
import { TranslateService } from "@ngx-translate/core";
import { DxDataGridComponent } from "devextreme-angular";
import { PayloadPacketFacade } from "../../../../classes/infrastructure/payloadpacket/payloadpacketfacade";
import { TransportData } from "../../../../classes/infrastructure/transportdata";
import { DashboardDrilldownFetchRequest } from "../../../../classes/domain/entities/website/dashboard/dashboarddrilldown/dashboarddrilldownfetchrequest";
import {
  DashboardDrilldownReport,
  DrillDownCardCount,
  DrillDownGrandTotals,
  DrillDownReportColumn,
  DrillDownRow
} from "../../../../classes/domain/entities/website/dashboard/dashboarddrilldown/dashboarddrilldownreport";
import { DRILLDOWN_TYPES_REQUIRING_SUBTYPE } from "../../../../classes/domain/entities/website/dashboard/dashboarddrilldown/dashboarddrilldowncontext.service";
import { DashboardFilterStateStorageService } from "../../../../services/dashboard-filter-state-storage.service";
import { exportDataGrid } from "devextreme/excel_exporter";
import { saveAs } from "file-saver";
import * as ExcelJS from "exceljs";
import { HierarchyFilterComponent } from "../../../../shared/components/hierarchy-filter/hierarchy-filter.component";
import { DashboardFilterService } from "../../../../services/dashboard_filter_dependent_multiselection.service";
import { debounceTime, distinctUntilChanged, map, Subject, takeUntil } from "rxjs";
import { BreadcrumbService } from "@app/services/breadcrumb.service";
import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import { SubDashboardResponseView } from "@app/classes/infrastructure/request_response/subdashboardresponseview";
import { SubDashboardFetchRequest } from "@app/classes/domain/entities/website/dashboard/dashboarddrilldown/subdashboardfetchrequest";

// ============================================================
// Sub-dashboard (summary cards) support
//
// Request keys sent to the backend:
//   1. drill_down_request_type        -> dashboard type            (always)
//   2. drill_down_sub_type            -> sub type, 0 if none       (always)
//   3. sub_dashboard_filter_by        -> level-1 card, 0 if none
//   4. sub_dashboard_child_filter_by  -> level-2 card, 0 if none
// ============================================================
export interface DrillDownCard {
  filterBy: number;   // sent as sub_dashboard_filter_by or sub_dashboard_child_filter_by
  label: string;      // translation key
  count: number;
}

interface DrillDownCardConfig {
  yearlyOnly: boolean;                // true: hide Filter Type/Month/Quarter/Half Year, force Yearly
  showAreaToggle: boolean;            // Rural/Urban switch
  countsFromSubDashboardApi: boolean; // true: counts come from sub_dashboard_fetch_request
  cardsTitleKey?: string;
  cards: DrillDownCard[];             // level 1 -> sub_dashboard_filter_by
  childCards?: { [parentFilterBy: number]: DrillDownCard[] }; // level 2 -> sub_dashboard_child_filter_by
}

// Death Register, Birth Register, Abortion, Miscarriage: Home | Hospital | Other, Hospital -> Private | Government
const LOCATION_CARD_CONFIG: DrillDownCardConfig = {
  yearlyOnly: false,
  showAreaToggle: false,
  countsFromSubDashboardApi: true,
  cardsTitleKey: "DASHBOARD.DRILL_DOWN.FAMILY_PLANNING_METHOD_USED",
  cards: [
    { filterBy: EventLocationTypes.Home, label: "DASHBOARD.DRILL_DOWN.LOCATION_HOME", count: 0 },
    { filterBy: EventLocationTypes.Hospital, label: "DASHBOARD.DRILL_DOWN.LOCATION_HOSPITAL", count: 0 },
    { filterBy: EventLocationTypes.Other, label: "DASHBOARD.DRILL_DOWN.LOCATION_OTHER", count: 0 },
  ],
  childCards: {
    [EventLocationTypes.Hospital]: [
      { filterBy: HospitalTypes.Private, label: "DASHBOARD.DRILL_DOWN.HOSPITAL_PRIVATE", count: 0 },
      { filterBy: HospitalTypes.Government, label: "DASHBOARD.DRILL_DOWN.HOSPITAL_GOVERNMENT", count: 0 },
    ],
  },
};

// Family Planning Usage: one row of method cards
const FAMILY_PLANNING_CARD_CONFIG: DrillDownCardConfig = {
  yearlyOnly: false,
  showAreaToggle: false,
  countsFromSubDashboardApi: true,
  cardsTitleKey: "DASHBOARD.DRILL_DOWN.FAMILY_PLANNING_METHOD_USED",
  cards: [
    { filterBy: ContraceptivePillsTypes.Chhaya, label: "DASHBOARD.DRILL_DOWN.CP_CHHAYA", count: 0 },
    { filterBy: ContraceptivePillsTypes.Condom, label: "DASHBOARD.DRILL_DOWN.CP_CONDOM", count: 0 },
    { filterBy: ContraceptivePillsTypes.Mala_N, label: "DASHBOARD.DRILL_DOWN.CP_MALA_N", count: 0 },
    { filterBy: ContraceptivePillsTypes.ECPills, label: "DASHBOARD.DRILL_DOWN.CP_EC_PILLS", count: 0 },
    { filterBy: ContraceptivePillsTypes.OralPills, label: "DASHBOARD.DRILL_DOWN.CP_ORAL_PILLS", count: 0 },
    { filterBy: ContraceptivePillsTypes.Antara, label: "DASHBOARD.DRILL_DOWN.CP_ANTARA", count: 0 },
    { filterBy: ContraceptivePillsTypes.PPIUCD, label: "DASHBOARD.DRILL_DOWN.CP_PPIUCD", count: 0 },
    { filterBy: ContraceptivePillsTypes.PAIUCD, label: "DASHBOARD.DRILL_DOWN.CP_PAIUCD", count: 0 },
  ],
};

const DRILLDOWN_CARDS: { [drillDownType: number]: DrillDownCardConfig } = {
  [DrillDownRequestTypes.children_0_to_6_yeras]: {
    yearlyOnly: true,
    showAreaToggle: true,
    countsFromSubDashboardApi: true, // counts arrive in res.Cards
    cards: [
      { filterBy: No_Of_ChildrenAgeTypes.children_age_0_1, label: "DASHBOARD.DRILL_DOWN.AGE_0_1", count: 0 },
      { filterBy: No_Of_ChildrenAgeTypes.children_age_1_2, label: "DASHBOARD.DRILL_DOWN.AGE_1_2", count: 0 },
      { filterBy: No_Of_ChildrenAgeTypes.children_age_2_3, label: "DASHBOARD.DRILL_DOWN.AGE_2_3", count: 0 },
      { filterBy: No_Of_ChildrenAgeTypes.children_age_3_4, label: "DASHBOARD.DRILL_DOWN.AGE_3_4", count: 0 },
      { filterBy: No_Of_ChildrenAgeTypes.children_age_4_5, label: "DASHBOARD.DRILL_DOWN.AGE_4_5", count: 0 },
      { filterBy: No_Of_ChildrenAgeTypes.children_age_5_6, label: "DASHBOARD.DRILL_DOWN.AGE_5_6", count: 0 },
    ],
  },
  [DrillDownRequestTypes.death_register]: LOCATION_CARD_CONFIG,
  [DrillDownRequestTypes.birth_register]: LOCATION_CARD_CONFIG,
  [DrillDownRequestTypes.pregnancy_loss]: LOCATION_CARD_CONFIG,
};
// Sterilized Couples: All | Male | Female
const STERILIZATION_CARD_CONFIG: DrillDownCardConfig = {
  yearlyOnly: false,
  showAreaToggle: false,
  countsFromSubDashboardApi: true,
  cardsTitleKey: "DASHBOARD.DRILL_DOWN.ECR_STERILIZED_COUPLES",
  cards: [
    { filterBy: SterilizedTypes.All, label: "DASHBOARD.DRILL_DOWN.STERILIZED_ALL", count: 0 },
    { filterBy: SterilizedTypes.Male, label: "DASHBOARD.DRILL_DOWN.STERILIZED_MALE", count: 0 },
    { filterBy: SterilizedTypes.Female, label: "DASHBOARD.DRILL_DOWN.STERILIZED_FEMALE", count: 0 },
  ],
};

// Which card is selected first, depending on what was clicked on the dashboard
const STERILIZATION_INITIAL_CARD: { [subType: number]: number } = {
  [DrillDownSubTypes.eligible_couple_register_sterilized_couples]: SterilizedTypes.All,
  [DrillDownSubTypes.eligible_couple_register_sterilized_couples_male]: SterilizedTypes.Male,
  [DrillDownSubTypes.eligible_couple_register_sterilized_couples_female]: SterilizedTypes.Female,
};

// Cards that depend on the sub type (checked before DRILLDOWN_CARDS)
const DRILLDOWN_CARDS_BY_SUBTYPE: { [subType: number]: DrillDownCardConfig } = {
  [DrillDownSubTypes.eligible_couple_register_family_planning_usage]: FAMILY_PLANNING_CARD_CONFIG,
  [DrillDownSubTypes.eligible_couple_register_sterilized_couples]: STERILIZATION_CARD_CONFIG,
  [DrillDownSubTypes.eligible_couple_register_sterilized_couples_male]: STERILIZATION_CARD_CONFIG,
  [DrillDownSubTypes.eligible_couple_register_sterilized_couples_female]: STERILIZATION_CARD_CONFIG,
};

@Component({
  selector: "app-dashboard-drilldown-report",
  templateUrl: "./dashboard-drilldown-report.component.html",
  styleUrl: "./dashboard-drilldown-report.component.scss",
  standalone: true,
  imports: [DevextremesharedmoduleModule, HierarchyFilterComponent],
})
export class DashboardDrillDownReportComponent implements OnInit, OnDestroy {
  pageSize: number = 0;
  isLoading: boolean = false;
  data: DrillDownRow[] = [];
  dataSource: any;
  columns: any[] = [];
  rawColumns: DrillDownReportColumn[] = [];
  leafCols: DrillDownReportColumn[] = [];
  summaryConfig: any[] = [];
  totalCount: number = 0;
  hasData: boolean = false;

  // Server-Side Remote Operations State
  currentSortColumn: string = "";
  currentSortDirection: string = "";
  request_date_time: string = ""; // Fixed query snapshot timestamp (YYYY-MM-DD-HH-mm-ss-000)
  search_text_placeholder: string = "Search here"; // Dynamic placeholder received from server
  searchText: string = ""; // Active search query applied
  currentSearchText: string = "";
  isNewRecordAvailable: boolean = false; // Flag from server indicating newer records exist
  isRefreshing: boolean = false; // Indicates active refresh in progress
  private searchSubject = new Subject<string>();

  CurrentFinancialYear: string = "";
  YearsList: FinancialYear[] = [];

  FilterList = DomainEnums.ReportFilterTypesListLang(
    true,
    "REPORT_PLACEHOLDERS.Select",
  ).filter((r) => r.Ref !== ReportFilterTypes.Custom);

  MonthList = DomainEnums.MonthListLang(
    true,
    "REPORT_PLACEHOLDERS.SelectMonth",
  );

  QuarterList = DomainEnums.QuarterTypesListLang(
    true,
    "REPORT_PLACEHOLDERS.SelectQuarter",
  );

  HalfYearList = DomainEnums.HalfYearListLang(
    true,
    "REPORT_PLACEHOLDERS.SelectHalfYear",
  );

  FinancialYearRef: number = 0;
  lastValidYear: number = 0;
  FilterRef: number = 0;
  Month: number = 0;
  Quarter: number = 0;
  HalfYear: number = 0;
  FromDate: string = "";
  ToDate: string = "";

  drillDownRequestType: number = 0;
  drillDownSubType: number = 0;

  LoginEmployeeRole: number = 0;
  LoginUserRef: number = 0;
  LoginPostRef: number = 0;

  // Hierarchy filter selections
  state_ids: number[] = [];
  zone_ids: number[] = [];
  district_ids: number[] = [];
  block_ids: number[] = [];
  bcm_post_ids: number[] = [];
  phc_ids: number[] = [];
  sc_ids: number[] = [];
  village_ids: number[] = [];
  af_post_ids: number[] = [];
  asha_employee_record_ids: number[] = [];

  financialYearStart!: Date;
  financialYearEnd!: Date;

  IsShowMonth: boolean = false;
  IsShowQuarter: boolean = false;
  IsShowHalfYear: boolean = false;
  IsShowDates: boolean = false;

  drillDownPageTitle = "DASHBOARD.DRILL_DOWN.DASHBOARD";
  Title: string = "";
  SubTitle: string = "";

  // ---------- sub-dashboard (cards) state ----------
  areaType: "rural" | "urban" = "rural";
  parentTitle = ""; // e.g. "Number Of Children(0-6 Years)"
  tableTitle = "";  // e.g. "1-2 Years Children" or "Hospital - Private"

  cards: DrillDownCard[] = []; // level 1 cards
  private childCardsByParent: { [parent: number]: DrillDownCard[] } = {};
  selectedCardValue = 0;       // -> sub_dashboard_filter_by
  selectedChildCardValue = 0;  // -> sub_dashboard_child_filter_by
  private isFetchingSubDashboardCounts = false;

  @ViewChild("dataGrid", { static: false }) dataGrid!: DxDataGridComponent;
  private destroy$ = new Subject<void>();

  get cardConfig(): DrillDownCardConfig | undefined {
    return (
      DRILLDOWN_CARDS_BY_SUBTYPE[this.drillDownSubType] ??
      DRILLDOWN_CARDS[this.drillDownRequestType]
    );
  }

  get cardsTitle(): string {
    const key = this.cardConfig?.cardsTitleKey;
    return key ? this.translate.instant(key) : this.parentTitle;
  }

  get showSummaryCards(): boolean { return !!this.cardConfig; }
  get isYearlyOnly(): boolean { return !!this.cardConfig?.yearlyOnly; }
  get showAreaToggle(): boolean { return !!this.cardConfig?.showAreaToggle; }

  // Level-2 cards of the currently selected level-1 card (e.g. Private | Government under Hospital)
  get childCards(): DrillDownCard[] {
    return this.childCardsByParent[this.selectedCardValue] ?? [];
  }
  // Level 2 exists but user hasn't picked yet -> don't load the grid
  get isAwaitingChildSelection(): boolean {
    return this.childCards.length > 0 && !this.selectedChildCardValue;
  }

  constructor(
    private uiUtils: UIUtils,
    private router: Router,
    private appStateManage: AppStateManageService,
    private screenSizeService: ScreenSizeService,
    private dtu: DTU,
    private CustomDate: FinancialYearUtilsService,
    private sidebarMenuState: SidebarMenuStateService,
    public langService: LanguageService,
    private translate: TranslateService,
    private filterStorage: DashboardFilterStateStorageService,
    public DashboardFilterService: DashboardFilterService,
    private breadcrumbService: BreadcrumbService,
  ) { }

  async ngOnInit() {
    // Capture consistent query snapshot timestamp when user enters the component
    this.request_date_time = this.generateCurrentTimeStamp();
    this.sidebarMenuState.role$
      .pipe(takeUntil(this.destroy$))
      .subscribe((role) => {
        this.LoginEmployeeRole = role ?? 0;
      });
    this.LoginUserRef = this.appStateManage.getLoginUserRef();
    this.LoginPostRef = this.appStateManage.getPostRef();

    const stored = this.appStateManage.StorageKey.getItem(
      "FinancialYearResponses",
    );
    const financialYearResponses = stored ? JSON.parse(stored) : [];
    if (financialYearResponses.length) {
      this.FinancialYearRef = financialYearResponses[0].financial_year_id;
      this.CurrentFinancialYear = financialYearResponses[0].short_name;
    }
    this.lastValidYear = this.FinancialYearRef || 0;

    await this.FormulateFinancialYearList();
    await this.FinancialYear(this.CurrentFinancialYear);
    this.pageSize = this.screenSizeService.getPageSize("withDropdown", 2);

    const ctx = this.filterStorage.getDrillDownContext();

    if (!ctx.drillDownType) {
      await this.uiUtils.showErrorMessage("Error", "Missing drill-down type.");
      await this.router.navigate(["home"]);
      return;
    }

    this.drillDownRequestType = ctx.drillDownType;
    this.drillDownSubType = ctx.drillDownSubType || 0;

    // Sub type is required for types listed in DRILLDOWN_TYPES_REQUIRING_SUBTYPE
    if (
      DRILLDOWN_TYPES_REQUIRING_SUBTYPE.includes(this.drillDownRequestType) &&
      !this.drillDownSubType
    ) {
      await this.uiUtils.showErrorMessage("Error", "Missing drill-down segment.");
      await this.router.navigate(["home"]);
      return;
    }

    // Card pages: build the card strips and select the first level-1 card
    if (this.cardConfig) {
      this.cards = this.cardConfig.cards.map((c) => ({ ...c }));
      this.childCardsByParent = {};
      Object.entries(this.cardConfig.childCards ?? {}).forEach(([parent, list]) => {
        this.childCardsByParent[+parent] = list.map((c) => ({ ...c }));
      });
      const initialCard = STERILIZATION_INITIAL_CARD[this.drillDownSubType];
      if (initialCard !== undefined) {
        // Male / Female click from the dashboard: open on that card,
        // and always send the parent sub type to the backend
        this.selectedCardValue = initialCard;
        this.drillDownSubType = DrillDownSubTypes.eligible_couple_register_sterilized_couples;
      } else {
        this.selectedCardValue = this.cards[0].filterBy;
      }
      this.selectedChildCardValue = 0;
    }

    this.drillDownPageTitle = this.getDrillDownPageTitle();
    this.updateTitles();
    this.setBreadcrumb();

    // remaining filter fields still come from the same object
    const state = this.filterStorage.getFilterState();
    if (state.FinancialYearRef) {
      this.FinancialYearRef = state.FinancialYearRef;
      this.lastValidYear = state.FinancialYearRef;
      const matchedYear = this.YearsList.find(
        (y) => y.p.id === state.FinancialYearRef,
      );
      if (matchedYear) this.CurrentFinancialYear = matchedYear.p.short_name;
    }

    if (this.isYearlyOnly) {
      // Filter Type dropdown is hidden on yearly-only pages -> always Yearly
      this.FilterRef = ReportFilterTypes.Yearly;
      this.Month = 0;
      this.Quarter = 0;
      this.HalfYear = 0;
    } else {
      this.FilterRef = state.ReportFilterType || ReportFilterTypes.Yearly;
      this.Month = state.Month || 0;
      this.Quarter = state.QuarterlyYearType || 0;
      this.HalfYear = state.HalfYearType || 0;
    }
    this.applyFilterVisibility(this.FilterRef);

    this.state_ids = state.state_ids ?? [];
    this.zone_ids = state.zone_ids ?? [];
    this.district_ids = state.district_ids ?? [];
    this.block_ids = state.block_ids ?? [];
    this.bcm_post_ids = state.bcm_post_ids ?? [];
    this.phc_ids = state.phc_ids ?? [];
    this.sc_ids = state.sc_ids ?? [];
    this.village_ids = state.village_ids ?? [];
    this.af_post_ids = state.af_ids ?? [];
    this.asha_employee_record_ids = state.asha_employee_record_ids ?? [];

    // Sync restored hierarchy filter IDs with DashboardFilterService
    if (this.state_ids.length) this.DashboardFilterService.state_ids = [...this.state_ids];
    if (this.zone_ids.length) this.DashboardFilterService.zone_ids = [...this.zone_ids];
    if (this.district_ids.length) this.DashboardFilterService.district_ids = [...this.district_ids];
    if (this.block_ids.length) this.DashboardFilterService.block_ids = [...this.block_ids];
    if (this.bcm_post_ids.length) this.DashboardFilterService.bcm_post_ids = [...this.bcm_post_ids];
    if (this.phc_ids.length) this.DashboardFilterService.phc_ids = [...this.phc_ids];
    if (this.sc_ids.length) this.DashboardFilterService.sc_ids = [...this.sc_ids];
    if (this.village_ids.length) this.DashboardFilterService.village_ids = [...this.village_ids];
    if (this.af_post_ids.length) this.DashboardFilterService.af_ids = [...this.af_post_ids];
    if (this.asha_employee_record_ids.length) this.DashboardFilterService.asha_ids = [...this.asha_employee_record_ids];

    // Setup debounced server-side search input listener
    this.searchSubject
      .pipe(debounceTime(350), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((text) => {
        this.searchText = text;
        this.refreshGrid(true);
      });

    this.translate.onLangChange
      .pipe(
        map((e) => e.lang),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.columns = [];
        this.data = [];
        this.FilterList = [...this.FilterList];
        this.MonthList = [...this.MonthList];
        this.QuarterList = [...this.QuarterList];
        this.HalfYearList = [...this.HalfYearList];
        this.updateTitles();
        this.setBreadcrumb();
        this.initDataSource();
      });

    this.initDataSource();

    // Sub-dashboard card counts (Home / Hospital / Other, Private / Government)
    if (this.cardConfig?.countsFromSubDashboardApi) {
      await this.fetchSubDashboardCounts();
    }
  }

  private setBreadcrumb(): void {
    this.breadcrumbService.set([
      { label: 'Home', labelKey: 'MENU.Dashboard', path: 'home' },
      { label: this.translate.instant(this.drillDownPageTitle) },
    ]);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================================
  // Sub-dashboard (cards) handlers
  // ============================================================
  onAreaChanged(area: "rural" | "urban") {
    if (this.areaType === area) return;
    this.areaType = area;
    this.refreshGrid(true);
  }

  // Level-1 card click (age group, or Home | Hospital | Other)
  async onCardClick(card: DrillDownCard) {
    if (this.selectedCardValue === card.filterBy) return;
    this.selectedCardValue = card.filterBy;
    this.selectedChildCardValue = 0; // always reset level 2
    this.updateTitles();
    if (this.cardConfig?.countsFromSubDashboardApi) {
      await this.fetchSubDashboardCounts();
    }
    // Hospital: wait until Private / Government is chosen
    if (!this.isAwaitingChildSelection) {
      this.refreshGrid(true);
    } else {
      this.hasData = false;
      this.refreshGrid(true); // clears the grid via the load() gate
    }
  }

  // Level-2 card click (Private | Government)
  async onChildCardClick(card: DrillDownCard) {
    if (this.selectedChildCardValue === card.filterBy) return;
    this.selectedChildCardValue = card.filterBy;
    this.updateTitles();
    await this.fetchSubDashboardCounts();
    this.refreshGrid(true);
  }

  private updateTitles() {
    this.parentTitle = this.translate.instant(this.drillDownPageTitle);
    const parent = this.cards.find((c) => c.filterBy === this.selectedCardValue);
    const child = this.childCards.find((c) => c.filterBy === this.selectedChildCardValue);
    this.tableTitle =
      [parent, child]
        .filter((c): c is DrillDownCard => !!c)
        .map((c) => this.translate.instant(c.label))
        .join(" - ") || this.parentTitle;
  }

  /**
   * Updates card counts after a grid fetch (only for pages whose counts come from the grid API).
   * - If the backend returns a `Cards` list ({ filter_by, count }), every card is updated.
   * - Otherwise only the active card is updated, using the row count of the table.
   */
  private applyCardCounts(res: DashboardDrilldownReport, rowCount: number) {
    if (!this.showSummaryCards || this.cardConfig?.countsFromSubDashboardApi) return;

    const backendCards: DrillDownCardCount[] = res.Cards ?? [];
    if (backendCards.length) {
      backendCards.forEach((bc) => {
        const card = this.cards.find((c) => c.filterBy === bc.filter_by);
        if (card) card.count = bc.count ?? 0;
      });
    } else {
      const active = this.cards.find((c) => c.filterBy === this.selectedCardValue);
      if (active) active.count = rowCount;
    }
    this.updateTitles();
  }

  // ============================================================
  // Request building (shared by the grid request and the count request)
  // ============================================================
  private pickIds(fromService: number[] | undefined, local: number[]): number[] {
    return fromService?.length ? fromService : local;
  }

  private applyCommonFilters(req: DashboardDrilldownFetchRequest | SubDashboardFetchRequest) {
    // --- the 4 keys ---
    req.drill_down_request_type = this.drillDownRequestType;
    req.drill_down_sub_type = this.drillDownSubType;                              // 0 if none
    req.sub_dashboard_filter_by = this.cardConfig ? this.selectedCardValue : 0;   // 0 if no cards
    req.sub_dashboard_child_filter_by = this.selectedChildCardValue;              // 0 unless level-2 selected

    // --- period ---
    req.login_user_id = this.LoginUserRef;
    req.financial_year_ids.push(this.FinancialYearRef);
    req.report_filter_type = this.FilterRef;
    if (this.Month) req.month = this.Month;
    if (this.Quarter) req.quarterly_year_type = this.Quarter;
    if (this.HalfYear) req.half_year_type = this.HalfYear;

    // --- hierarchy ---
    const f = this.DashboardFilterService;
    req.state_ids = [...this.pickIds(f.state_ids, this.state_ids)];
    req.zone_ids = [...this.pickIds(f.zone_ids, this.zone_ids)];
    req.district_ids = [...this.pickIds(f.district_ids, this.district_ids)];
    req.block_ids = [...this.pickIds(f.block_ids, this.block_ids)];
    req.bcm_post_ids = [...this.pickIds(f.bcm_post_ids, this.bcm_post_ids)];
    req.phc_ids = [...this.pickIds(f.phc_ids, this.phc_ids)];
    req.sc_ids = [...this.pickIds(f.sc_ids, this.sc_ids)];
    req.village_ids = [...this.pickIds(f.village_ids, this.village_ids)];

    const afIds = new Set<number>(this.pickIds(f.af_ids, this.af_post_ids));
    if (this.LoginEmployeeRole === Roles.AF) afIds.add(this.LoginPostRef);
    req.af_post_ids = [...afIds];

    const ashaIds = new Set<number>(this.pickIds(f.asha_ids, this.asha_employee_record_ids));
    if (this.LoginEmployeeRole === Roles.Asha) ashaIds.add(this.LoginUserRef);
    req.asha_employee_record_ids = [...ashaIds];
  }

  private buildSubDashboardCountRequest(): SubDashboardFetchRequest {
    const req = new SubDashboardFetchRequest();
    this.applyCommonFilters(req);
    req.request_date_time = this.request_date_time;
    return req;
  }

  /**
   * Builds the grid request with remote pagination, sorting, search and hierarchy filter IDs.
   */
  private buildFetchRequest(
    skip: number = 0,
    take: number = 20,
    orderByColumn: string = "",
    orderDirection: string = "",
    searchText: string = "",
  ): DashboardDrilldownFetchRequest {
    const req = new DashboardDrilldownFetchRequest();
    this.applyCommonFilters(req);

    if (this.FromDate)
      req.from_date = this.dtu.ConvertStringDateToFullFormat(this.FromDate);
    if (this.ToDate)
      req.to_date = this.dtu.DateEndStringFromDateValue(new Date(this.ToDate));

    req.skip = skip;
    req.take = take;
    req.order_by_column = orderByColumn;
    req.order_direction = orderDirection;
    req.search_text = searchText;
    req.request_date_time = this.request_date_time;

    return req;
  }

  // ============================================================
  // Sub-dashboard counts
  // Backend: sub_dashboard_fetch_request -> sub_dashboard_response_view
  // ============================================================
  private async fetchSubDashboardCounts(): Promise<void> {
    if (!this.cardConfig?.countsFromSubDashboardApi) return;
    if (!this.FinancialYearRef || !this.LoginUserRef) return;
    if (this.isFetchingSubDashboardCounts) return; // already running - drop duplicate

    this.isFetchingSubDashboardCounts = true;
    try {
      const pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(
        this.buildSubDashboardCountRequest().FormulateTransportData(),
      );
      const tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
      if (!tr.Successful) return;

      const tdResponse = JSON.parse(tr.Tag) as TransportData;
      const rows = SubDashboardResponseView.FromTransportData(tdResponse);

      console.log('[SubDashboard] Mapped rows (SubDashboardResponseView[]):', rows);
      if (rows.length) {
        this.applySubDashboardCounts(rows);
      }
    } catch (err) {
      console.error("[DrillDown] Sub-dashboard count fetch failed:", err);
    } finally {
      this.isFetchingSubDashboardCounts = false;
    }
  }

  // Backend returns a `level` per row: 1 = level-1 card, 2 = level-2 card
  private applySubDashboardCounts(rows: SubDashboardResponseView[]): void {
    if (!rows?.length) return;

    // Level 1: top-level rows
    this.cards.forEach((card) => {
      card.count = rows.find((r) => r.enum_value === card.filterBy)?.count ?? 0;
    });

    // Level 2: look inside the matching parent's sub_list
    Object.entries(this.childCardsByParent).forEach(([parentKey, children]) => {
      const parentRow = rows.find((r) => r.enum_value === +parentKey);
      children.forEach((child) => {
        child.count = parentRow?.sub_list.find((s) => s.enum_value === child.filterBy)?.count ?? 0;
      });
    });

    this.updateTitles();
  }

  onSearchChanged(text: string) {
    this.searchSubject.next(text || "");
  }

  getDrillDownPageTitle(): string {
    // ============================================================
    // Types having sub-types
    // ============================================================
    switch (this.drillDownRequestType) {
      // ------------------------------------------------------------
      // Family Registration
      // ------------------------------------------------------------
      case DrillDownRequestTypes.family_registration:
        switch (this.drillDownSubType) {
          case DrillDownSubTypes.family_registration_approved:
            return "DASHBOARD.DRILL_DOWN.FAMILY_REGISTRATION_APPROVED";

          case DrillDownSubTypes.family_registration_pending:
            return "DASHBOARD.DRILL_DOWN.FAMILY_REGISTRATION_PENDING";

          case DrillDownSubTypes.family_registration_approved_native:
            return "DASHBOARD.DRILL_DOWN.FAMILY_REGISTRATION_APPROVED_NATIVE";

          case DrillDownSubTypes.family_registration_approved_migrated:
            return "DASHBOARD.DRILL_DOWN.FAMILY_REGISTRATION_APPROVED_MIGRATED";

          case DrillDownSubTypes.family_registration_pending_native:
            return "DASHBOARD.DRILL_DOWN.FAMILY_REGISTRATION_PENDING_NATIVE";

          case DrillDownSubTypes.family_registration_pending_migrated:
            return "DASHBOARD.DRILL_DOWN.FAMILY_REGISTRATION_PENDING_MIGRATED";

          case DrillDownSubTypes.family_registration_total:
          default:
            return "DASHBOARD.DRILL_DOWN.FAMILY_REGISTRATION";
        }

      // ------------------------------------------------------------
      // ANC
      // ------------------------------------------------------------
      case DrillDownRequestTypes.anc:
        switch (this.drillDownSubType) {
          case DrillDownSubTypes.anc_total:
            return "DASHBOARD.DRILL_DOWN.ANC_REGISTERED";

          case DrillDownSubTypes.anc_fully_protected_anc:
            return "DASHBOARD.DRILL_DOWN.ANC_FULLY_PROTECTED";

          case DrillDownSubTypes.anc_normal_maata:
            return "DASHBOARD.DRILL_DOWN.ANC_NORMAL";

          case DrillDownSubTypes.anc_expected_deliveries:
            return "DASHBOARD.DRILL_DOWN.ANC_EXPECTED_DELIVERIES";

          case DrillDownSubTypes.anc_pregnancy_registered_within_12_weeks:
            return "DASHBOARD.DRILL_DOWN.ANC_PREGNANCY_REGISTERED_WITHIN_12_WEEKS";

          case DrillDownSubTypes.anc_high_risk_maata:
            return "DASHBOARD.DRILL_DOWN.ANC_HIGH_RISK";

          case DrillDownSubTypes.anc_severe_anemia:
            return "DASHBOARD.DRILL_DOWN.ANC_SEVERE_ANEMIA";

          default:
            return "DASHBOARD.DRILL_DOWN.ANC";
        }

      // ------------------------------------------------------------
      // PNC
      // ------------------------------------------------------------
      case DrillDownRequestTypes.pnc:
        switch (this.drillDownSubType) {
          case DrillDownSubTypes.pnc_total:
            return "DASHBOARD.DRILL_DOWN.PNC_REGISTERED";

          case DrillDownSubTypes.pnc_total_pnc_visits_completed:
            return "DASHBOARD.DRILL_DOWN.PNC_COMPLETED";

          case DrillDownSubTypes.pnc_high_risk_pnc:
            return "DASHBOARD.DRILL_DOWN.PNC_HIGH_RISK";

          case DrillDownSubTypes.pnc_normal_pnc:
            return "DASHBOARD.DRILL_DOWN.PNC_NORMAL";

          case DrillDownSubTypes.pnc_zero_visit_completed:
            return "DASHBOARD.DRILL_DOWN.PNC_ZERO_VISITS_COMPLETED";

          default:
            return "DASHBOARD.DRILL_DOWN.PNC";
        }

      // ------------------------------------------------------------
      // HBNC
      // ------------------------------------------------------------
      case DrillDownRequestTypes.hbnc:
        switch (this.drillDownSubType) {
          case DrillDownSubTypes.hbnc_total_birth:
            return "DASHBOARD.DRILL_DOWN.HBNC_TOTAL_BIRTHS";

          case DrillDownSubTypes.hbnc_total_register:
            return "DASHBOARD.DRILL_DOWN.HBNC_TOTAL_REGISTER";

          case DrillDownSubTypes.hbnc_total_visits_completed:
            return "DASHBOARD.DRILL_DOWN.HBNC_TOTAL_VISITS_COMPLETED";

          case DrillDownSubTypes.hbnc_zero_visits_completed:
            return "DASHBOARD.DRILL_DOWN.HBNC_ZERO_VISITS_COMPLETED";

          case DrillDownSubTypes.hbnc_defective_children:
            return "DASHBOARD.DRILL_DOWN.HBNC_DISABLED_CHILDREN";

          case DrillDownSubTypes.hbnc_child_death:
            return "DASHBOARD.DRILL_DOWN.HBNC_CHILD_DEATH";

          case DrillDownSubTypes.hbnc_nicu:
            return "DASHBOARD.DRILL_DOWN.HBNC_NICU";

          case DrillDownSubTypes.hbnc_nbsu:
            return "DASHBOARD.DRILL_DOWN.HBNC_NBSU";

          case DrillDownSubTypes.hbnc_sncu:
            return "DASHBOARD.DRILL_DOWN.HBNC_SNCU";

          default:
            return "DASHBOARD.DRILL_DOWN.HBNC";
        }

      // ------------------------------------------------------------
      // Eligible Couple Register
      // ------------------------------------------------------------
      case DrillDownRequestTypes.eligible_couple_register:
        switch (this.drillDownSubType) {
          case DrillDownSubTypes.eligible_couple_register_eligible_couples_total:
            return "DASHBOARD.DRILL_DOWN.ECR_ELIGIBLE_COUPLES";

          case DrillDownSubTypes.eligible_couple_register_infertile:
            return "DASHBOARD.DRILL_DOWN.ECR_INFERTILE";

          case DrillDownSubTypes.eligible_couple_register_sterilized_couples:
            return "DASHBOARD.DRILL_DOWN.ECR_STERILIZED_COUPLES";

          case DrillDownSubTypes.eligible_couple_register_sterilized_couples_male:
            return "DASHBOARD.DRILL_DOWN.ECR_STERILIZED_COUPLES_MALE";

          case DrillDownSubTypes.eligible_couple_register_sterilized_couples_female:
            return "DASHBOARD.DRILL_DOWN.ECR_STERILIZED_COUPLES_FEMALE";

          case DrillDownSubTypes.eligible_couple_register_family_planning_usage:
            return "DASHBOARD.DRILL_DOWN.ECR_USING_FAMILY_PLANNING";

          default:
            return "DASHBOARD.DRILL_DOWN.ELIGIBLE_COUPLE_REGISTER";
        }

      // ------------------------------------------------------------
      // Birth Register
      // ------------------------------------------------------------
      case DrillDownRequestTypes.birth_register:
        switch (this.drillDownSubType) {
          case DrillDownSubTypes.birth_total:
            return "DASHBOARD.DRILL_DOWN.BIRTH_TOTAL";

          case DrillDownSubTypes.birth_boy:
            return "DASHBOARD.DRILL_DOWN.BIRTH_MALE";

          case DrillDownSubTypes.birth_girl:
            return "DASHBOARD.DRILL_DOWN.BIRTH_FEMALE";

          default:
            return "DASHBOARD.DRILL_DOWN.BIRTH_REGISTER";
        }

      // ------------------------------------------------------------
      // Child Immunization
      // ------------------------------------------------------------
      case DrillDownRequestTypes.child_immunization:
        switch (this.drillDownSubType) {
          case DrillDownSubTypes.immunization_fully:
            return "DASHBOARD.DRILL_DOWN.IMMUNIZATION_FULLY";

          case DrillDownSubTypes.immunization_zero_dose:
            return "DASHBOARD.DRILL_DOWN.IMMUNIZATION_ZERO_DOSE";

          case DrillDownSubTypes.immunization_started_not_completed:
            return "DASHBOARD.DRILL_DOWN.IMMUNIZATION_STARTED_NOT_COMPLETED";

          case DrillDownSubTypes.immunization_partial:
            return "DASHBOARD.DRILL_DOWN.IMMUNIZATION_PARTIAL";

          default:
            return "DASHBOARD.DRILL_DOWN.CHILD_IMMUNIZATION";
        }

      // ------------------------------------------------------------
      // Death Register
      // ------------------------------------------------------------
      case DrillDownRequestTypes.death_register:
        switch (this.drillDownSubType) {
          case DrillDownSubTypes.death_total:
            return "DASHBOARD.DRILL_DOWN.DEATH_TOTAL";

          case DrillDownSubTypes.death_anc_death:
            return "DASHBOARD.DRILL_DOWN.DEATH_ANC";

          case DrillDownSubTypes.death_still_birth:
            return "DASHBOARD.DRILL_DOWN.DEATH_STILL_BIRTH";

          case DrillDownSubTypes.death_0_to_5_years_death:
            return "DASHBOARD.DRILL_DOWN.DEATH_0_TO_5_YEARS";

          case DrillDownSubTypes.death_above_30_years_death:
            return "DASHBOARD.DRILL_DOWN.DEATH_30_PLUS_YEARS";

          case DrillDownSubTypes.death_above_65_years_death:
            return "DASHBOARD.DRILL_DOWN.DEATH_65_PLUS_YEARS";

          default:
            return "DASHBOARD.DRILL_DOWN.DEATH_REGISTER";
        }

      // ------------------------------------------------------------
      // Pregnancy Loss
      // ------------------------------------------------------------
      case DrillDownRequestTypes.pregnancy_loss:
        switch (this.drillDownSubType) {
          case DrillDownSubTypes.pregnancy_loss_abortion:
            return "DASHBOARD.DRILL_DOWN.PREGNANCY_LOSS_ABORTION";

          case DrillDownSubTypes.pregnancy_loss_miscarriage:
            return "DASHBOARD.DRILL_DOWN.PREGNANCY_LOSS_MISCARRIAGE";

          default:
            return "DASHBOARD.DRILL_DOWN.PREGNANCY_LOSS";
        }
    }

    // ============================================================
    // Types without sub-types
    // ============================================================
    switch (this.drillDownRequestType) {
      case DrillDownRequestTypes.total_population_covered_by_asha:
        return "DASHBOARD.DRILL_DOWN.TOTAL_POPULATION_COVERED_BY_ASHA";

      case DrillDownRequestTypes.no_of_familes:
        return "DASHBOARD.DRILL_DOWN.NUMBER_OF_FAMILIES";

      case DrillDownRequestTypes.children_0_to_6_yeras:
        return "DASHBOARD.DRILL_DOWN.CHILDREN_0_6_YEARS";

      case DrillDownRequestTypes.member_above_30_years:
        return "DASHBOARD.DRILL_DOWN.MEMBERS_ABOVE_30_YEARS";

      case DrillDownRequestTypes.member_above_65_years:
        return "DASHBOARD.DRILL_DOWN.MEMBERS_ABOVE_65_YEARS";

      default:
        return "DASHBOARD.DRILL_DOWN.DASHBOARD";
    }
  }

  FinancialYear = async (FinancialYear: string) => {
    const range = this.CustomDate.getFinancialYearRange(FinancialYear);
    if (range) {
      this.financialYearStart = range.start;
      this.financialYearEnd = range.end;
    }
  };

  async BackToDashboard() {
    this.filterStorage.clearDrillDownContext();
    // await this.router.navigate(["home"]);
    await this.router.navigate(["dashboard"]);
  }

  FormulateFinancialYearList = async () => {
    let lst = await FinancialYear.FetchEntireList(
      async (errMsg) => await this.uiUtils.showErrorMessage("Error", errMsg),
    );
    this.YearsList = lst;
  };

  applyFilterVisibility(filterType: number) {
    this.IsShowMonth = filterType === ReportFilterTypes.Monthly;
    this.IsShowQuarter = filterType === ReportFilterTypes.Quarterly;
    this.IsShowHalfYear = filterType === ReportFilterTypes.HalfYearly;
    this.IsShowDates = filterType === ReportFilterTypes.Custom;
  }

  onYearChanged(e: any) {
    const newValue = e.value;
    if (!newValue || newValue === 0) {
      e.component.option("value", this.lastValidYear);
      return;
    }
    this.FinancialYearRef = newValue;
    this.lastValidYear = newValue;

    const selectedYear = this.YearsList.find((y) => y.p.id === newValue);
    if (!selectedYear) return;

    this.CurrentFinancialYear = selectedYear.p.short_name;

    this.filterStorage.setFilterState({
      FinancialYearRef: this.FinancialYearRef,
    });

    // Skip initial bind (same as grid)
    if (
      (this.FinancialYearRef && e.previousValue == 0) ||
      e.previousValue == null
    ) {
      return;
    }

    this.refreshGrid(true);
    if (this.showSummaryCards) {
      void this.fetchSubDashboardCounts();
    }
  }

  OnFilterChange = async (FilterRef: number) => {
    if (!FilterRef) return;

    this.FilterRef = FilterRef;
    this.Month = 0;
    this.Quarter = 0;
    this.HalfYear = 0;

    this.applyFilterVisibility(FilterRef);

    this.filterStorage.setFilterState({ ReportFilterType: FilterRef });

    if (FilterRef === ReportFilterTypes.Yearly) {
      this.refreshGrid(true);
      if (this.showSummaryCards) {
        await this.fetchSubDashboardCounts();
      }
    }
  };

  onMonthChanged = async (e: any) => {
    if (!e.value) return;
    this.Month = e.value;
    this.filterStorage.setFilterState({ Month: this.Month });
    this.refreshGrid(true);
    if (this.showSummaryCards) {
      await this.fetchSubDashboardCounts();
    }
  };

  onQuarterChanged = async (e: any) => {
    if (!e.value) return;
    this.Quarter = e.value;
    this.filterStorage.setFilterState({ QuarterlyYearType: this.Quarter });
    this.refreshGrid(true);
    if (this.showSummaryCards) {
      await this.fetchSubDashboardCounts();
    }
  };

  onHalfYearChanged = async (e: any) => {
    if (!e.value) return;
    this.HalfYear = e.value;
    this.filterStorage.setFilterState({ HalfYearType: this.HalfYear });
    this.refreshGrid(true);
    if (this.showSummaryCards) {
      await this.fetchSubDashboardCounts();
    }
  };

  getText(en: string, mr: string): string {
    return this.langService.currentLang === "mr" ? mr : en;
  }

  // Base title used for on-screen/export titles.
  // Card pages: "<parent> - <selected card(s)>"
  private getBaseTitle(): string {
    const parent = this.translate.instant(this.drillDownPageTitle);
    if (this.showSummaryCards && this.tableTitle) {
      return `${parent} - ${this.tableTitle}`;
    }
    return parent;
  }

  // Build dynamic Title/SubTitle based on active filter
  private buildFilterTitle() {
    const baseTitle = this.getBaseTitle();

    if (this.FilterRef === ReportFilterTypes.Yearly) {
      this.Title = this.getText(`${baseTitle} Yearly`, `${baseTitle} वार्षिक`);
      this.SubTitle = this.CurrentFinancialYear;
    }

    if (this.FilterRef === ReportFilterTypes.Monthly && this.Month) {
      const m = this.MonthList.find((x) => x.Ref === this.Month);
      if (m) {
        const monthName = this.translate.instant(m.Name);
        this.Title = this.getText(
          `${baseTitle} Monthly (${monthName})`,
          `${baseTitle} मासिक (${monthName})`,
        );
        this.SubTitle = `${monthName} - ${this.CurrentFinancialYear}`;
      }
    }

    if (this.FilterRef === ReportFilterTypes.Quarterly && this.Quarter) {
      const q = this.QuarterList.find((x) => x.Ref === this.Quarter);
      if (q) {
        const quarterName = this.translate.instant(q.Name);
        this.Title = this.getText(
          `${baseTitle} Quarterly (${quarterName})`,
          `${baseTitle} तिमाही (${quarterName})`,
        );
        this.SubTitle = `${quarterName} - ${this.CurrentFinancialYear}`;
      }
    }

    if (this.FilterRef === ReportFilterTypes.HalfYearly && this.HalfYear) {
      const h = this.HalfYearList.find((x) => x.Ref === this.HalfYear);
      if (h) {
        const halfName = this.translate.instant(h.Name);
        this.Title = this.getText(
          `${baseTitle} Half Yearly (${halfName})`,
          `${baseTitle} सहामाही (${halfName})`,
        );
        this.SubTitle = `${halfName} - ${this.CurrentFinancialYear}`;
      }
    }

    if (
      this.FilterRef === ReportFilterTypes.Custom &&
      this.FromDate &&
      this.ToDate
    ) {
      const from = this.dtu.ToShortDisplayDate(this.FromDate);
      const to = this.dtu.ToShortDisplayDate(this.ToDate);
      this.Title = this.getText(
        `${baseTitle} (From ${from} To ${to})`,
        `${baseTitle} (${from} ते ${to})`,
      );
      this.SubTitle = `${from} - ${to}`;
    }
  }

  // ── Fetch + display ──

  /**
   * Executes HTTP request to fetch drill-down report.
   */
  private async sendDrillDownRequest(
    req: DashboardDrilldownFetchRequest,
  ): Promise<DashboardDrilldownReport | null> {
    const pktRequest =
      PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(
        req.FormulateTransportData(),
      );
    const tr =
      await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);

    if (!tr.Successful) {
      await this.uiUtils.showInformationalMessage("Error", tr.Message);
      return null;
    }

    const tdResponse = JSON.parse(tr.Tag) as TransportData;
    return DashboardDrilldownReport.FromTransportData(tdResponse);
  }

  /**
   * Initializes DevExtreme CustomStore DataSource configured for remote operations:
   * - Remote paging (skip, take)
   * - Remote sorting (order_by_column, order_direction)
   * - Remote search filtering (search_text)
   */
  public initDataSource() {
    this.buildFilterTitle();

    this.dataSource = new DataSource({
      store: new CustomStore({
        key: undefined,
        load: async (loadOptions: any) => {
          if (!this.FinancialYearRef || !this.FilterRef || !this.LoginUserRef) {
            this.hasData = false;
            return { data: [], totalCount: 0 };
          }

          // Hospital selected but Private / Government not chosen yet -> no grid
          if (this.isAwaitingChildSelection) {
            this.hasData = false;
            this.data = [];
            this.totalCount = 0;
            return { data: [], totalCount: 0 };
          }

          // 1. Pagination parameters
          const skip = loadOptions.skip || 0;
          const take = loadOptions.take || this.pageSize || 20;

          // 2. Sorting parameters
          let orderByColumn = "";
          let orderDirection = "";
          if (loadOptions.sort && loadOptions.sort.length > 0) {
            orderByColumn = loadOptions.sort[0].selector || "";
            orderDirection = loadOptions.sort[0].desc ? "desc" : "asc";
          }
          this.currentSortColumn = orderByColumn;
          this.currentSortDirection = orderDirection;

          // 3. Search parameters
          let searchText = "";
          if (loadOptions.searchValue !== undefined && loadOptions.searchValue !== null) {
            searchText = String(loadOptions.searchValue).trim();
          } else if (this.searchText) {
            searchText = this.searchText.trim();
          }
          this.currentSearchText = searchText;

          const req = this.buildFetchRequest(
            skip,
            take,
            orderByColumn,
            orderDirection,
            searchText,
          );
          this.isLoading = true;

          try {
            const res = await this.sendDrillDownRequest(req);
            if (!res) {
              this.hasData = false;
              return { data: [], totalCount: 0 };
            }

            // Check if server indicated new records are available
            this.isNewRecordAvailable = !!res.IsNewRecordAvailable;

            // Update dynamic search placeholder from server if provided
            if (res.SearchTextPlaceholder) {
              this.search_text_placeholder = res.SearchTextPlaceholder;
            }

            // Setup dynamic columns only if columns changed or not yet initialized
            const columnsChanged =
              !this.columns ||
              this.columns.length === 0 ||
              this.haveColumnsChanged(this.rawColumns, res.Columns);
            if (columnsChanged && res.Columns && res.Columns.length > 0) {
              this.setupColumns(res.Columns);
            }

            let returnedRows: DrillDownRow[] = res.Rows ?? [];
            let total = res.TotalRowsCount;

            // Graceful fallback if backend returned all rows (unpaginated on server)
            if (total == null || total === 0) {
              if (returnedRows.length > take && take > 0) {
                total = returnedRows.length;
                returnedRows = returnedRows.slice(skip, skip + take);
              } else {
                total = returnedRows.length;
              }
            }

            // Update summary card counts (grid-driven pages only)
            this.applyCardCounts(res, total);

            // Map rows and compute sequential Sr. No across pages (skip + rowIndex + 1)
            const mappedData = this.mapRows(returnedRows, skip);
            this.data = mappedData;
            this.totalCount = total;
            this.hasData = total > 0;

            this.buildSummary(mappedData, res.GrandTotals ?? undefined);

            return {
              data: mappedData,
              totalCount: total,
            };
          } catch (err) {
            console.error("Error loading drilldown data:", err);
            return { data: [], totalCount: 0 };
          } finally {
            this.isLoading = false;
          }
        },
      }),
    });
  }

  public refreshGrid(resetPage: boolean = false) {
    this.buildFilterTitle();
    if (this.dataGrid?.instance) {
      if (resetPage && this.dataGrid.instance.pageIndex() !== 0) {
        this.dataGrid.instance.pageIndex(0);
      } else {
        this.dataGrid.instance.refresh();
      }
    } else if (this.dataSource) {
      this.dataSource.reload();
    } else {
      this.initDataSource();
    }
  }

  public GetDrillDownReport = async () => {
    this.refreshGrid(true);
  };

  /**
   * Triggered by Refresh button.
   * Generates a new request_date_time snapshot to fetch the latest records from the database.
   */
  async refreshReport(): Promise<void> {
    this.isRefreshing = true;
    this.isNewRecordAvailable = false;
    this.request_date_time = this.generateCurrentTimeStamp();
    try {
      if (this.showSummaryCards) {
        await this.fetchSubDashboardCounts();
      }
      this.refreshGrid(false);
    } finally {
      setTimeout(() => {
        this.isRefreshing = false;
      }, 600);
    }
  }

  private generateCurrentTimeStamp(): string {
    const d = new Date();
    d.setMilliseconds(0);
    return this.dtu.ConvertToString(d);
  }

  private haveColumnsChanged(prev: DrillDownReportColumn[], next: DrillDownReportColumn[]): boolean {
    if (!prev || !next) return true;
    if (prev.length !== next.length) return true;
    for (let i = 0; i < prev.length; i++) {
      if (prev[i]?.field !== next[i]?.field || prev[i]?.caption !== next[i]?.caption) {
        return true;
      }
    }
    return false;
  }

  private setupColumns(serverColumns: DrillDownReportColumn[]) {
    this.rawColumns = serverColumns;
    this.columns = this.applyIndianFormat(this.transformColumns(serverColumns));
    this.leafCols = this.flattenLeafColumns(serverColumns);
  }

  private transformColumns(cols: DrillDownReportColumn[]): any[] {
    return cols.map((col) => {
      if (col.child_column && col.child_column.length > 0) {
        return {
          caption: col.caption,
          alignment: "center",
          columns: this.transformColumns(col.child_column),
        };
      } else {
        return {
          dataField: col.field,
          name: col.field,
          caption: col.caption,
          dataType: (this.mapdata_type(col.data_type) || "string")
            .toString()
            .trim(),
          alignment: "center",
          allowSearch: true,
          filterOperations: [
            "contains",
            "startswith",
            "endswith",
            "=",
            "<>",
            ">",
            "<",
          ],
          selectedFilterOperation: "contains",
        };
      }
    });
  }

  private flattenLeafColumns(cols: DrillDownReportColumn[]): DrillDownReportColumn[] {
    const out: DrillDownReportColumn[] = [];
    const walk = (arr: DrillDownReportColumn[]) => {
      arr.forEach((c) => {
        if (c.child_column && c.child_column.length > 0) {
          walk(c.child_column);
        } else {
          out.push(c);
        }
      });
    };
    walk(cols);
    return out;
  }

  private applyIndianFormat(cols: any[]): any[] {
    return cols.map((col) => {
      if (col.columns && col.columns.length > 0) {
        return { ...col, columns: this.applyIndianFormat(col.columns) };
      }
      if (col.dataType === "number") {
        return {
          ...col,
          customizeText: (cellInfo: any) =>
            cellInfo.value != null
              ? new Intl.NumberFormat("en-IN", {
                maximumFractionDigits: 2,
              }).format(cellInfo.value)
              : "-",
        };
      }
      return col;
    });
  }

  private mapRows(rows: DrillDownRow[], skip: number = 0): DrillDownRow[] {
    if (!rows || rows.length === 0 || !this.leafCols || this.leafCols.length === 0) {
      return [];
    }
    return rows.map((row, rowIndex) => {
      const mapped: DrillDownRow = {};
      const keys = Object.keys(row);
      let backendIndex = 0;
      this.leafCols.forEach((leaf, leafIndex) => {
        if (leafIndex === 0) {
          mapped[leaf.field] = skip + rowIndex + 1;
          backendIndex = 1;
          return;
        }
        if (row[leaf.field] !== undefined) {
          mapped[leaf.field] = row[leaf.field];
        } else if (keys[backendIndex] !== undefined) {
          mapped[leaf.field] = row[keys[backendIndex]];
          backendIndex++;
        } else {
          mapped[leaf.field] = "";
        }
      });
      return mapped;
    });
  }

  private buildSummary(rows: DrillDownRow[], serverGrandTotals?: DrillDownGrandTotals) {
    if (!this.leafCols || this.leafCols.length === 0) return;
    const blockColumn = this.leafCols.find(
      (c) => this.mapdata_type(c.data_type).trim() === "string",
    );
    const incentiveColumns = this.leafCols.filter(
      (c, i) => i > 0 && this.mapdata_type(c.data_type).trim() === "number",
    );

    if (!blockColumn || incentiveColumns.length === 0) {
      this.summaryConfig = [];
      return;
    }

    let grandTotals: DrillDownGrandTotals = {};
    if (serverGrandTotals) {
      grandTotals = serverGrandTotals;
    } else {
      const isBlockRow = (row: DrillDownRow) =>
        String(row[blockColumn.field] ?? "").trim() !== "";

      const blockWiseTotals = new Map<string, Record<string, number>>();
      rows.forEach((row) => {
        if (!isBlockRow(row)) return;
        const block = String(row[blockColumn.field]);
        if (!blockWiseTotals.has(block)) blockWiseTotals.set(block, {});
        const totals = blockWiseTotals.get(block)!;
        incentiveColumns.forEach((col) => {
          const value = Number(row[col.field]) || 0;
          totals[col.field] = (totals[col.field] || 0) + value;
        });
      });

      incentiveColumns.forEach((col) => (grandTotals[col.field] = 0));
      blockWiseTotals.forEach((blockTotal) => {
        incentiveColumns.forEach((col) => {
          grandTotals[col.field] += blockTotal[col.field] || 0;
        });
      });
    }

    this.summaryConfig = [
      {
        column: blockColumn.field,
        displayFormat: "Total",
        showInColumn: blockColumn.field,
      },
      ...incentiveColumns.map((col) => ({
        column: col.field,
        showInColumn: col.field,
        customizeText: () =>
          new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(
            grandTotals[col.field] || 0,
          ),
      })),
    ];
  }

  private mapdata_type(typeCode: number): string {
    switch (typeCode) {
      case 10:
        return "string ";
      case 20:
      case 30:
      case 40:
      case 50:
        return "number";
      default:
        return "string";
    }
  }

  onCellPrepared(e: any) {
    if (e.rowType === "data" && e.column.caption === "Sr. No") {
      const pageIndex = e.component.pageIndex();
      const pageSize = e.component.pageSize();
      e.cellElement.innerText = pageIndex * pageSize + e.rowIndex + 1;
    }
  }

  getTranslatedName = (item: any): string =>
    item ? this.translate.instant(item.Name) : "";

  formatExportText(value: string): string {
    return value
      ?.replace(/\s+/g, "_")
      ?.replace(/[()–-]/g, "")
      ?.replace(/__+/g, "_")
      ?.trim();
  };

  // Dynamic Export File Name Based On Selected Filters
  private getExportReportName() {
    let baseName = this.formatExportText(this.getBaseTitle());

    let name = baseName;

    if (this.FilterRef === ReportFilterTypes.Yearly) {
      name += `_${this.CurrentFinancialYear}`;
    }

    if (this.FilterRef === ReportFilterTypes.Monthly && this.Month) {
      const m = this.MonthList.find((x) => x.Ref === this.Month);
      if (m) {
        const monthName = this.formatExportText(this.translate.instant(m.Name));
        name += `_${monthName}_${this.CurrentFinancialYear}`;
      }
    }

    if (this.FilterRef === ReportFilterTypes.Quarterly && this.Quarter) {
      const q = this.QuarterList.find((x) => x.Ref === this.Quarter);
      if (q) {
        const quarterName = this.formatExportText(
          this.translate.instant(q.Name),
        );
        name += `_${quarterName}_${this.CurrentFinancialYear}`;
      }
    }

    if (this.FilterRef === ReportFilterTypes.HalfYearly && this.HalfYear) {
      const h = this.HalfYearList.find((x) => x.Ref === this.HalfYear);
      if (h) {
        const halfName = this.formatExportText(this.translate.instant(h.Name));
        name += `_${halfName}_${this.CurrentFinancialYear}`;
      }
    }

    if (
      this.FilterRef === ReportFilterTypes.Custom &&
      this.FromDate &&
      this.ToDate
    ) {
      const from = this.dtu
        .ToShortDisplayDate(this.FromDate)
        .replace(/\//g, "-");
      const to = this.dtu.ToShortDisplayDate(this.ToDate).replace(/\//g, "-");
      name += `_From_${from}_To_${to}`;
    }

    const now = new Date();
    const today = now.toLocaleDateString("en-GB").replace(/\//g, "-"); // DD-MM-YYYY

    const hours = now.getHours();
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");

    const ampm = hours >= 12 ? "PM" : "AM";
    const hr12 = hours % 12 === 0 ? 12 : hours % 12;
    const time12 = `${hr12}-${minutes}-${seconds}_${ampm}`;

    name += `_${today}_${time12}`;

    const sheetName = name.substring(0, 31); // Excel sheet name limit
    const fileName = name;
    return { sheetName, fileName };
  };

  async exportToExcel() {
    this.isLoading = true;
    try {
      const { sheetName, fileName } = this.getExportReportName();
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(sheetName);

      // Fetch complete dataset with take: 0 (or all records) for export
      const exportReq = this.buildFetchRequest(
        0,
        0,
        this.currentSortColumn,
        this.currentSortDirection,
        this.currentSearchText,
      );
      const res = await this.sendDrillDownRequest(exportReq);
      const exportRows = res?.Rows ? this.mapRows(res.Rows, 0) : this.data;

      const colCount = this.leafCols.length > 0 ? this.leafCols.length : (this.dataGrid?.instance?.columnCount() || 1);
      const lastCol = worksheet.getColumn(colCount).letter;

      worksheet.mergeCells(`A2:${lastCol}2`);
      worksheet.getCell("A2").value = this.Title || this.getBaseTitle();
      worksheet.getCell("A2").alignment = { horizontal: "center" };
      worksheet.getCell("A2").font = { size: 16, bold: true };

      worksheet.mergeCells(`A3:${lastCol}3`);
      worksheet.getCell("A3").value = this.SubTitle || this.CurrentFinancialYear;
      worksheet.getCell("A3").alignment = { horizontal: "center" };
      worksheet.getCell("A3").font = { size: 12 };

      worksheet.getRow(4).values = [];

      if (this.totalCount <= this.data.length && this.dataGrid?.instance) {
        await exportDataGrid({
          component: this.dataGrid.instance,
          worksheet: worksheet,
          topLeftCell: { row: 5, column: 1 },
          autoFilterEnabled: true,
        });
      } else {
        const headerRow = worksheet.getRow(5);
        this.leafCols.forEach((col, idx) => {
          const cell = headerRow.getCell(idx + 1);
          cell.value = col.caption;
          cell.font = { bold: true };
          cell.alignment = { horizontal: "center" };
        });

        exportRows.forEach((row, rIdx) => {
          const excelRow = worksheet.getRow(6 + rIdx);
          this.leafCols.forEach((col, cIdx) => {
            const cell = excelRow.getCell(cIdx + 1);
            cell.value = row[col.field] ?? "";
            cell.alignment = { horizontal: "center" };
          });
        });
      }

      worksheet.views = [{ state: "frozen", ySplit: 6 }];

      const rowCount = worksheet.rowCount;
      const totalGridCols = this.leafCols.length > 0 ? this.leafCols.length : (this.dataGrid?.instance?.columnCount() || 1);

      for (let i = 5; i <= rowCount; i++) {
        for (let j = 1; j <= totalGridCols; j++) {
          const cell = worksheet.getRow(i).getCell(j);
          cell.border = {
            top: { style: "thin", color: { argb: "000000" } },
            left: { style: "thin", color: { argb: "000000" } },
            bottom: { style: "thin", color: { argb: "000000" } },
            right: { style: "thin", color: { argb: "000000" } },
          };
        }
      }

      worksheet.protect("VT_REPORT_LOCK_2026", {
        selectLockedCells: false,
        selectUnlockedCells: false,
        autoFilter: true,
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `${fileName}.xlsx`);
    } catch (err) {
      console.error("Export error:", err);
      await this.uiUtils.showErrorMessage("Export Error", "Failed to export data to Excel.");
    } finally {
      this.isLoading = false;
    }
  }

  // For Filter Popup — opens instantly, then loads cascade data
  async openFilterPanel(): Promise<void> {
    this.DashboardFilterService.openFilter();
    await this.DashboardFilterService.LoadFilterDataUPTOStateLevel();
  }

  // Called by <app-hierarchy-filter (applied)="..."> after user hits Apply
  onFiltersApplied = async () => {
    this.state_ids = [...this.DashboardFilterService.state_ids];
    this.zone_ids = [...this.DashboardFilterService.zone_ids];
    this.district_ids = [...this.DashboardFilterService.district_ids];
    this.block_ids = [...this.DashboardFilterService.block_ids];
    this.bcm_post_ids = [...this.DashboardFilterService.bcm_post_ids];
    this.phc_ids = [...this.DashboardFilterService.phc_ids];
    this.sc_ids = [...this.DashboardFilterService.sc_ids];
    this.village_ids = [...this.DashboardFilterService.village_ids];
    this.af_post_ids = [...this.DashboardFilterService.af_ids];
    this.asha_employee_record_ids = [...this.DashboardFilterService.asha_ids];

    this.filterStorage.setFilterState({
      FinancialYearRef: this.FinancialYearRef,
      ReportFilterType: this.FilterRef,
      Month: this.Month,
      QuarterlyYearType: this.Quarter,
      HalfYearType: this.HalfYear,
      state_ids: this.state_ids,
      zone_ids: this.zone_ids,
      district_ids: this.district_ids,
      block_ids: this.block_ids,
      bcm_post_ids: this.bcm_post_ids,
      phc_ids: this.phc_ids,
      af_ids: this.af_post_ids,
      sc_ids: this.sc_ids,
      village_ids: this.village_ids,
      asha_ids: this.asha_employee_record_ids,
      asha_employee_record_ids: this.asha_employee_record_ids,
    });
    if (this.showSummaryCards) {
      await this.fetchSubDashboardCounts();
    }
    this.refreshGrid(true);
  };

  // Called after user hits Clear — service state is already reset by then
  onFiltersCleared = () => {
    this.state_ids = [];
    this.zone_ids = [];
    this.district_ids = [];
    this.block_ids = [];
    this.bcm_post_ids = [];
    this.phc_ids = [];
    this.sc_ids = [];
    this.village_ids = [];
    this.af_post_ids = [];
    this.asha_employee_record_ids = [];

    this.filterStorage.setFilterState({
      FinancialYearRef: this.FinancialYearRef,
      ReportFilterType: this.FilterRef,
      Month: this.Month,
      QuarterlyYearType: this.Quarter,
      HalfYearType: this.HalfYear,
      state_ids: [],
      zone_ids: [],
      district_ids: [],
      block_ids: [],
      bcm_post_ids: [],
      phc_ids: [],
      af_ids: [],
      sc_ids: [],
      village_ids: [],
      asha_ids: [],
      asha_employee_record_ids: [],
    });
    if (this.showSummaryCards) {
      void this.fetchSubDashboardCounts();
    }
    this.refreshGrid(true);
  };
}
